import { Controller } from '@hotwired/stimulus';

// Controller pour valider les trades sélectionnés par rapport aux trades du contractor
// Affiche une modale si des trades manquants sont détectés à la soumission du formulaire
export default class extends Controller {
  static targets = ['contractorSelect', 'tradeCheckbox', 'modalMessage', 'confirmButton'];

  static values = {
    projectId: String,
  };

  connect() {
    this.formSubmitted = false;
  }

  // Récupère le controller de la modale
  get modalController() {
    const modalElement = document.getElementById('trade-validation-modal');
    return modalElement?.modalController;
  }

  // Récupère le formulaire
  get formElement() {
    return this.element.querySelector('form');
  }

  // Intercepte la soumission du formulaire pour valider les trades
  handleSubmit(event) {
    // Si on a déjà validé (après avoir cliqué sur un des boutons de la modale), laisser passer
    if (this.formSubmitted) {
      return;
    }

    const contractorId = this.contractorSelectTarget.value;

    // Si aucun contractor sélectionné, laisser Rails gérer l'erreur de validation
    if (!contractorId) {
      return;
    }

    // Récupérer les trades du contractor (supporte <select> et combobox)
    const tradesData = this.findContractorTradesData(contractorId);
    if (!tradesData) {
      return;
    }

    const contractorTrades = JSON.parse(tradesData.trades || '[]');
    const selectedTrades = this.getSelectedTrades();

    // Identifier les trades manquants
    const missingTrades = selectedTrades.filter((trade) => !contractorTrades.includes(trade));

    // Si des trades sont manquants, empêcher la soumission et afficher la modale
    if (missingTrades.length > 0) {
      event.preventDefault();
      this.showModal(missingTrades, contractorId, tradesData.name);
    }
  }

  // Récupère tous les trades sélectionnés. Exclut `data-orphan="true"` : lots hors liste de
  // l'agence, dont les lots propres au marché, jamais proposés à la fiche entreprise.
  getSelectedTrades() {
    return Array.from(this.tradeCheckboxTargets)
      .filter((checkbox) => checkbox.checked && checkbox.dataset.orphan !== 'true')
      .map((checkbox) => checkbox.value);
  }

  // Affiche la modale avec la liste des trades manquants (DOM-based for XSS safety)
  showModal(missingTrades, contractorId, contractorName) {
    const container = this.modalMessageTarget;
    container.replaceChildren();

    const intro = document.createElement('p');
    intro.className = 'text-sm mb-3';
    intro.textContent = "Le(s) corps d'état suivant(s) ne sont pas renseignés sur la fiche de ";
    const strong = document.createElement('strong');
    strong.textContent = contractorName;
    intro.appendChild(strong);
    intro.appendChild(document.createTextNode(' :'));
    container.appendChild(intro);

    const ul = document.createElement('ul');
    ul.className = 'list-disc list-inside mb-4 bg-neutral-50 p-3 rounded border border-neutral-200';
    missingTrades.forEach((trade) => {
      const li = document.createElement('li');
      li.className = 'text-sm';
      li.textContent = trade;
      ul.appendChild(li);
    });
    container.appendChild(ul);

    const outro = document.createElement('p');
    outro.className = 'text-sm';
    outro.textContent = "Voulez-vous les ajouter automatiquement à l'entreprise ?";
    container.appendChild(outro);

    // Stocker les infos pour la confirmation
    this.missingTrades = missingTrades;
    this.contractorId = contractorId;

    // Ouvrir la modale via le controller existant
    if (this.modalController) {
      this.modalController.open();
    }
  }

  // Crée le marché sans ajouter les trades au contractor
  createWithoutAdding() {
    // Fermer la modale
    if (this.modalController) {
      this.modalController.close();
    }

    // Marquer comme soumis pour éviter la ré-validation
    this.formSubmitted = true;

    // Soumettre le formulaire
    this.formElement.requestSubmit();
  }

  // Ajoute les trades au contractor puis crée le marché
  async addAndCreate() {
    try {
      // Appeler l'action du contrôleur pour ajouter les trades
      const response = await fetch(
        `/projects/${this.projectIdValue}/contracts/add_trades_to_contractor`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
          },
          body: JSON.stringify({
            contractor_id: this.contractorId,
            trades: this.missingTrades,
          }),
        }
      );

      // Un corps non JSON (500 HTML, proxy) ne doit pas masquer le message serveur d'un 422.
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        // 422 ou autre erreur : on affiche le message et on NE crée PAS le marché.
        this.showErrorInModal(
          data.message || "Impossible d'ajouter les corps d'état à l'entreprise."
        );
        return;
      }

      if (data.success) {
        // Mettre à jour l'attribut data-trades du contractor
        const tradesElement = this.findContractorTradesElement(this.contractorId);
        if (tradesElement) {
          const currentTrades = JSON.parse(tradesElement.dataset.trades || '[]');
          const updatedTrades = [...new Set([...currentTrades, ...this.missingTrades])];
          tradesElement.dataset.trades = JSON.stringify(updatedTrades);
        }

        // Fermer la modale via le controller existant
        if (this.modalController) {
          this.modalController.close();
        }

        // Afficher un message de succès
        this.showSuccessMessage("Corps d'état ajoutés avec succès à l'entreprise");

        // Marquer comme soumis et soumettre le formulaire
        this.formSubmitted = true;
        this.formElement.requestSubmit();
      } else {
        this.showErrorInModal(data.message || "Une erreur inattendue s'est produite.");
      }
    } catch (error) {
      console.error("Erreur lors de l'ajout des trades:", error);
      this.showErrorInModal("Une erreur s'est produite lors de l'ajout des corps d'état.");
    }
  }

  // Affiche un message d'erreur dans la modale (remplace le contenu)
  showErrorInModal(message) {
    const container = this.modalMessageTarget;
    container.replaceChildren();

    const errorDiv = document.createElement('div');
    errorDiv.className = 'bg-danger-50 border border-danger-200 rounded-lg p-4';

    const errorText = document.createElement('p');
    errorText.className = 'text-sm text-danger-700';
    errorText.textContent = message;
    errorDiv.appendChild(errorText);

    container.appendChild(errorDiv);
  }

  // Trouve l'élément portant data-trades pour un contractor (supporte <select> et combobox)
  findContractorTradesElement(contractorId) {
    // Try <select> option first
    const option = this.contractorSelectTarget.querySelector(
      `option[value="${CSS.escape(contractorId)}"]`
    );
    if (option) return option;

    // Try combobox <li> option (sibling element with data-value)
    const comboboxRoot = this.contractorSelectTarget.closest(
      '[data-controller="contractor-combobox"]'
    );
    if (comboboxRoot) {
      return comboboxRoot.querySelector(`[data-value="${CSS.escape(contractorId)}"]`);
    }
    return null;
  }

  // Retourne { trades, name } pour un contractor
  findContractorTradesData(contractorId) {
    const element = this.findContractorTradesElement(contractorId);
    if (!element) return null;

    // <option> element: text is .text, trades is data-trades
    if (element.tagName === 'OPTION') {
      return { trades: element.dataset.trades, name: element.text.trim() };
    }

    // Combobox <li>: name is in .block.truncate span, trades is data-trades
    const nameSpan = element.querySelector('.block.truncate');
    return {
      trades: element.dataset.trades,
      name: nameSpan ? nameSpan.textContent.trim() : '',
    };
  }

  // Affiche un message de succès temporaire
  showSuccessMessage(message) {
    const alertDiv = document.createElement('div');
    alertDiv.className =
      'fixed top-4 right-4 bg-success-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
    alertDiv.textContent = message;
    document.body.appendChild(alertDiv);

    setTimeout(() => {
      alertDiv.remove();
    }, 3000);
  }
}
